<template>
  <Dialog :title="dialogTitle" v-model="dialogVisible" width="720px">
    <el-form
      ref="formRef"
      :model="formData"
      :rules="formRules"
      label-width="100px"
      v-loading="formLoading"
    >
      <el-form-item label="品牌名称" prop="name">
        <el-input v-model="formData.name" placeholder="请输入品牌名称" />
      </el-form-item>
      <el-form-item label="品牌 Logo" prop="picUrl">
        <UploadImg v-model="formData.picUrl" :limit="1" :is-show-tip="false" />
      </el-form-item>
      <el-form-item label="品牌分类" prop="categoryId">
        <ProductCategorySelect v-model="formData.categoryId" />
        <div class="form-tip">用于品牌联盟页分类 Tab 筛选</div>
      </el-form-item>
      <el-form-item label="详情大图" prop="detailPicUrl">
        <UploadImg v-model="formData.detailPicUrl" :limit="1" :is-show-tip="false" />
        <div class="form-tip">品牌详情页左侧大图，不填则使用 Logo</div>
      </el-form-item>
      <el-form-item label="品牌排序" prop="sort">
        <el-input-number v-model="formData.sort" controls-position="right" :min="0" />
      </el-form-item>
      <el-form-item label="品牌状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="品牌简介" prop="description">
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="3"
          placeholder="详情页标题下方的简介文案"
        />
      </el-form-item>
      <el-form-item label="服务热线" prop="contactPhone">
        <el-input v-model="formData.contactPhone" placeholder="请输入服务热线" />
      </el-form-item>
      <el-form-item label="联系邮箱" prop="contactEmail">
        <el-input v-model="formData.contactEmail" placeholder="请输入联系邮箱" />
      </el-form-item>
      <el-form-item label="地址" prop="address">
        <el-input v-model="formData.address" placeholder="请输入地址" />
      </el-form-item>
      <el-form-item label="品牌故事" prop="brandStory">
        <el-input
          v-model="formData.brandStory"
          type="textarea"
          :rows="6"
          placeholder="请输入品牌故事（支持换行）"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitForm" type="primary" :disabled="formLoading">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { CommonStatusEnum } from '@/utils/constants'
import * as ProductBrandApi from '@/api/mall/product/brand'
import ProductCategorySelect from '@/views/mall/product/category/components/ProductCategorySelect.vue'

defineOptions({ name: 'ProductBrandForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const formData = ref({
  id: undefined,
  name: '',
  picUrl: '',
  categoryId: undefined,
  detailPicUrl: '',
  sort: 0,
  status: CommonStatusEnum.ENABLE,
  description: '',
  contactPhone: '',
  contactEmail: '',
  address: '',
  brandStory: ''
})
const formRules = reactive({
  name: [{ required: true, message: '品牌名称不能为空', trigger: 'blur' }],
  picUrl: [{ required: true, message: '品牌图片不能为空', trigger: 'blur' }],
  sort: [{ required: true, message: '品牌排序不能为空', trigger: 'blur' }]
})
const formRef = ref() // 表单 Ref

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  // 修改时，设置数据
  if (id) {
    formLoading.value = true
    try {
      formData.value = await ProductBrandApi.getBrand(id)
    } finally {
      formLoading.value = false
    }
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  // 提交请求
  formLoading.value = true
  try {
    const data = formData.value as ProductBrandApi.BrandVO
    if (formType.value === 'create') {
      await ProductBrandApi.createBrand(data)
      message.success(t('common.createSuccess'))
    } else {
      await ProductBrandApi.updateBrand(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    name: '',
    picUrl: '',
    categoryId: undefined,
    detailPicUrl: '',
    sort: 0,
    status: CommonStatusEnum.ENABLE,
    description: '',
    contactPhone: '',
    contactEmail: '',
    address: '',
    brandStory: ''
  }
  formRef.value?.resetFields()
}
</script>

<style scoped>
.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
  line-height: 1.4;
}
</style>
